// Board topology. The bow points left; see docs/unfathomable/rules-outline.md §4.

export type WaterSpaceId = "W14" | "Wu3" | "Wu5" | "Wu7" | "W58" | "Wl4" | "Wl6" | "Wl8";
export type DeckSpaceId = "D1" | "D2" | "D3" | "D4" | "D5" | "D6" | "D7" | "D8";
export type InteriorSpaceId = "bridge" | "chapel" | "cabin" | "cargo" | "galley" | "boiler" | "sickbay" | "brig";
export type SpaceId = WaterSpaceId | DeckSpaceId | InteriorSpaceId;
export type ShipSpaceId = DeckSpaceId | InteriorSpaceId;

export type SpaceKind = "water" | "deck" | "interior";

export interface SpaceInfo {
  id: SpaceId;
  kind: SpaceKind;
  nameZh: string;
  nameEn: string;
  /** Printed numbers; used for die matching and "highest number" tie breaks. */
  numbers: number[];
  /** Deep One movement arrow (water -> deck -> interior). */
  arrow?: SpaceId;
}

const S = (id: SpaceId, kind: SpaceKind, nameZh: string, nameEn: string, numbers: number[], arrow?: SpaceId): SpaceInfo =>
  ({ id, kind, nameZh, nameEn, numbers, arrow });

export const kSpaces: Record<SpaceId, SpaceInfo> = {
  W14: S("W14", "water", "前方水域(上)", "Front Water 1-4", [1, 2, 3, 4], "D1"),
  Wu3: S("Wu3", "water", "上舷水域(前)", "Upper Water 3", [], "D3"),
  Wu5: S("Wu5", "water", "上舷水域(后)", "Upper Water 5", [], "D5"),
  Wu7: S("Wu7", "water", "后方水域(上)", "Back Water 7", [], "D7"),
  W58: S("W58", "water", "前方水域(下)", "Front Water 5-8", [5, 6, 7, 8], "D2"),
  Wl4: S("Wl4", "water", "下舷水域(前)", "Lower Water 4", [], "D4"),
  Wl6: S("Wl6", "water", "下舷水域(后)", "Lower Water 6", [], "D6"),
  Wl8: S("Wl8", "water", "后方水域(下)", "Back Water 8", [], "D8"),
  D1: S("D1", "deck", "甲板 1", "Deck 1", [1], "bridge"),
  D2: S("D2", "deck", "甲板 2", "Deck 2", [2], "bridge"),
  D3: S("D3", "deck", "甲板 3", "Deck 3", [3], "chapel"),
  D4: S("D4", "deck", "甲板 4", "Deck 4", [4], "cabin"),
  D5: S("D5", "deck", "甲板 5", "Deck 5", [5], "cargo"),
  D6: S("D6", "deck", "甲板 6", "Deck 6", [6], "galley"),
  D7: S("D7", "deck", "甲板 7", "Deck 7", [7], "boiler"),
  D8: S("D8", "deck", "甲板 8", "Deck 8", [8], "boiler"),
  bridge: S("bridge", "interior", "舰桥", "Bridge", [1, 2]),
  chapel: S("chapel", "interior", "礼拜堂", "Chapel", [3]),
  cabin: S("cabin", "interior", "船长室", "Captain's Cabin", [4]),
  cargo: S("cargo", "interior", "货舱", "Cargo Hold", [5]),
  galley: S("galley", "interior", "厨房", "Galley", [6]),
  boiler: S("boiler", "interior", "锅炉房", "Boiler Room", [7, 8]),
  sickbay: S("sickbay", "interior", "医务室", "Sick Bay", []),
  brig: S("brig", "interior", "禁闭室", "Brig", []),
};

export const kSpaceIds = Object.keys(kSpaces) as SpaceId[];
export const kWaterSpaces: WaterSpaceId[] = ["W14", "Wu3", "Wu5", "Wu7", "W58", "Wl4", "Wl6", "Wl8"];
export const kDeckSpaces: DeckSpaceId[] = ["D1", "D2", "D3", "D4", "D5", "D6", "D7", "D8"];
export const kInteriorSpaces: InteriorSpaceId[] = ["bridge", "chapel", "cabin", "cargo", "galley", "boiler", "sickbay", "brig"];
/** Interior spaces that can be damaged and entered by Deep Ones. */
export const kDamageableSpaces: InteriorSpaceId[] = ["bridge", "chapel", "cabin", "cargo", "galley", "boiler"];
export const kShipSpaces: ShipSpaceId[] = [...kDeckSpaces, ...kInteriorSpaces];

export const kFrontWater: WaterSpaceId[] = ["W14", "W58"];
export const kBackWater: WaterSpaceId[] = ["Wu7", "Wl8"];

/** Each side's water chain, front to back. */
const kWaterChains: WaterSpaceId[][] = [["W14", "Wu3", "Wu5", "Wu7"], ["W58", "Wl4", "Wl6", "Wl8"]];

/** Next water space toward the back, or "deep" for the back spaces. */
export function waterTowardBack(w: WaterSpaceId): WaterSpaceId | "deep" {
  for (const chain of kWaterChains) {
    const i = chain.indexOf(w);
    if (i >= 0) return i === chain.length - 1 ? "deep" : chain[i + 1];
  }
  throw new Error(`not a water space: ${w}`);
}

/** Next water space toward the front (front spaces stay put). */
export function waterTowardFront(w: WaterSpaceId): WaterSpaceId {
  for (const chain of kWaterChains) {
    const i = chain.indexOf(w);
    if (i >= 0) return i === 0 ? chain[0] : chain[i - 1];
  }
  throw new Error(`not a water space: ${w}`);
}

const kEdges: [SpaceId, SpaceId][] = [
  // water chains + bow/stern seams
  ["W14", "Wu3"], ["Wu3", "Wu5"], ["Wu5", "Wu7"],
  ["W58", "Wl4"], ["Wl4", "Wl6"], ["Wl6", "Wl8"],
  ["W14", "W58"], ["Wu7", "Wl8"],
  // water - deck
  ["W14", "D1"], ["Wu3", "D3"], ["Wu5", "D5"], ["Wu7", "D7"],
  ["W58", "D2"], ["Wl4", "D4"], ["Wl6", "D6"], ["Wl8", "D8"],
  // deck ring
  ["D1", "D2"], ["D1", "D3"], ["D3", "D5"], ["D5", "D7"], ["D7", "D8"],
  ["D2", "D4"], ["D4", "D6"], ["D6", "D8"],
  // deck - interior
  ["D1", "bridge"], ["D2", "bridge"], ["D3", "chapel"], ["D4", "cabin"],
  ["D5", "cargo"], ["D6", "galley"], ["D7", "boiler"], ["D8", "boiler"],
  // interior
  ["bridge", "chapel"], ["bridge", "cabin"], ["chapel", "cabin"],
  ["chapel", "sickbay"], ["cabin", "sickbay"],
  ["chapel", "cargo"], ["cabin", "galley"], ["cargo", "galley"],
  ["cargo", "brig"], ["galley", "brig"],
  ["cargo", "boiler"], ["galley", "boiler"],
];

const kAdjacency: Record<SpaceId, SpaceId[]> = Object.fromEntries(kSpaceIds.map(id => [id, [] as SpaceId[]])) as any;
for (const [a, b] of kEdges) {
  kAdjacency[a].push(b);
  kAdjacency[b].push(a);
}

export function adjacentSpaces(id: SpaceId): SpaceId[] {
  return kAdjacency[id];
}

export function isAdjacent(a: SpaceId, b: SpaceId): boolean {
  return kAdjacency[a].includes(b);
}

export function highestNumber(id: SpaceId): number {
  const n = kSpaces[id].numbers;
  return n.length === 0 ? 0 : Math.max(...n);
}

/** BFS distance between damageable interior spaces (Brig / Sick Bay are not traversable). */
export function interiorDistances(from: InteriorSpaceId): Map<InteriorSpaceId, number> {
  const dist = new Map<InteriorSpaceId, number>([[from, 0]]);
  const queue: InteriorSpaceId[] = [from];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    for (const n of kAdjacency[cur]) {
      if (!(kDamageableSpaces as SpaceId[]).includes(n)) continue;
      const ni = n as InteriorSpaceId;
      if (dist.has(ni)) continue;
      dist.set(ni, dist.get(cur)! + 1);
      queue.push(ni);
    }
  }
  return dist;
}

/**
 * Among `candidates`, the one closest to `from`; ties broken by highest printed number.
 * Returns null if no candidate is reachable.
 */
export function closestInterior(from: InteriorSpaceId, candidates: InteriorSpaceId[]): InteriorSpaceId | null {
  const dist = interiorDistances(from);
  let best: InteriorSpaceId | null = null;
  for (const c of candidates) {
    const d = dist.get(c);
    if (d === undefined) continue;
    if (best === null) { best = c; continue; }
    const bd = dist.get(best)!;
    if (d < bd || (d === bd && highestNumber(c) > highestNumber(best))) best = c;
  }
  return best;
}

/** The next step from `from` toward `target` along the interior graph (prefers higher-numbered steps on ties). */
export function stepToward(from: InteriorSpaceId, target: InteriorSpaceId): InteriorSpaceId {
  const distToTarget = interiorDistances(target);
  let best: InteriorSpaceId | null = null;
  for (const n of kAdjacency[from]) {
    if (!(kDamageableSpaces as SpaceId[]).includes(n)) continue;
    const ni = n as InteriorSpaceId;
    const d = distToTarget.get(ni);
    if (d === undefined) continue;
    if (best === null || d < distToTarget.get(best)! || (d === distToTarget.get(best)! && highestNumber(ni) > highestNumber(best))) {
      best = ni;
    }
  }
  if (best === null) throw new Error(`no path from ${from} to ${target}`);
  return best;
}

export function interiorForDie(n: number): InteriorSpaceId {
  if (n <= 2) return "bridge";
  if (n === 3) return "chapel";
  if (n === 4) return "cabin";
  if (n === 5) return "cargo";
  if (n === 6) return "galley";
  return "boiler";
}

export function deckForDie(n: number): DeckSpaceId {
  return `D${n}` as DeckSpaceId;
}

export function frontWaterForDie(n: number): WaterSpaceId {
  return n <= 4 ? "W14" : "W58";
}

/** Mother Hydra damages the interior reached by following arrows from her water space. */
export function hydraTargetInterior(w: WaterSpaceId): InteriorSpaceId {
  const deck = kSpaces[w].arrow!;
  return kSpaces[deck].arrow as InteriorSpaceId;
}

export function spaceKind(id: SpaceId): SpaceKind {
  return kSpaces[id].kind;
}

export function spaceName(id: SpaceId | "deep"): string {
  return id === "deep" ? "深渊" : kSpaces[id].nameZh;
}
